/**
 * @file testimonialsSection.component.tsx
 * @path apps/website/src/components/sections/testimonialsSection.component.tsx
 * @description
 */

import { testimonialsData } from "@/data/testimonials";
import { Button } from "@sirbastians/ui";
import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TestimonialCard } from "../common/testimonialCard.component";
import { sectionSpacing } from "@/config";
import { cn } from "@sirbastians/utils";

const TestimonialsSection = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const { scrollYProgress } = useScroll({
    target: scrollContainerRef,
    offset: ["start start", "end end"],
  });

  const y1 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["0%", "-10%"] : ["0%", "-25%"],
  );
  const y2 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? ["-10%", "0%"] : ["-25%", "0%"],
  );

  // MODIFIED: Conditionally slice the data based on screen size
  const testimonialsToShow = isMobile
    ? testimonialsData.slice(0, 3) // Show 3 testimonials on mobile
    : testimonialsData.slice(0, 6); // Show 6 testimonials on desktop

  const half = Math.ceil(testimonialsToShow.length / 2);
  const testimonialsCol1 = testimonialsToShow.slice(0, half);
  const testimonialsCol2 = testimonialsToShow.slice(half);

  return (
    <section className={cn(sectionSpacing, "py-24")}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-56 space-y-4 sm:space-y-6 text-center lg:text-left py-24">
            <h2 className="text-3xl font-bold sm:text-4xl text-primary italic leading-tight ">
              Don't take it <br className="hidden lg:block" /> from us.
            </h2>
            <p className="text-base sm:text-lg font-sans text-muted-foreground leading-relaxed max-w-md mx-auto lg:mx-0">
              Here's why Sir Bastian's is the Shuttle Service people always
              remember for unforgettable transport and tours.
            </p>
            <Button asChild>
              <a
                href="https://share.google/xgak0ZZk3lDK1Hq1s"
                target="_blank"
                rel="noopener noreferrer"
              >
                Send a Review
              </a>
            </Button>
          </div>
        </div>

        {isMobile ? (
          // Mobile: Simple scrollable list (will now only show 3)
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {testimonialsToShow.map((testimonial) => (
                <TestimonialCard key={testimonial.author} {...testimonial} />
              ))}
            </div>
          </div>
        ) : (
          // Desktop: Parallax scroll layout (will show 6)
          <div
            ref={scrollContainerRef}
            className="lg:col-span-2 h-[300vh] relative"
          >
            <div className="sticky top-0 h-screen flex items-center">
              <div className="relative w-full h-[600px] overflow-hidden">
                <div className="absolute -top-1 left-0 w-full h-36 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-full h-36 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                  <motion.div style={{ y: y1 }} className="flex flex-col">
                    {testimonialsCol1.map((testimonial) => (
                      <TestimonialCard
                        key={testimonial.author}
                        {...testimonial}
                      />
                    ))}
                  </motion.div>
                  <motion.div style={{ y: y2 }} className="flex flex-col">
                    {testimonialsCol2.map((testimonial) => (
                      <TestimonialCard
                        key={testimonial.author}
                        {...testimonial}
                      />
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
